import React, { createContext, useState, useEffect, ReactNode } from "react";
import {
  User,
  onAuthStateChanged,
  signOut,
  verifyPasswordResetCode,
  confirmPasswordReset,
  sendPasswordResetEmail,
  applyActionCode,
  signInWithEmailLink,
} from "@firebase/auth";
import { auth } from "config/firebase";
import { handleLoginWithGoogle } from "core/auth/handleLoginWithGoogle";
import { handleLoginWithEmail } from "core/auth/handleLoginWithEmail";
import HyperDX from "@hyperdx/browser";
import { getAuthUserProfile } from "core/auth/getAuthUserProfile";
import {
  PrivateUserInfo,
  Freelancer,
  Stakeholder,
  IPrivateUserInfo,
} from "@freetech/models/user";
import { LoginCredentials, UserRole } from "@freetech/models";
import { MultiFactorError } from "@firebase/auth";
import { apiClient } from "@/api/apiClient";
import { FirebaseError } from "@firebase/util";
import { EXPIRATION_TIME_MINUTES } from "../constants";
import { useHyperDxAction } from "@/hooks/common/useHyperDxAction";

type IAuthContext = {
  currentUser?: User | null;
  userInfo?: IPrivateUserInfo | Stakeholder | Freelancer;
  role?: UserRole;
  isLoading: boolean;
  setIsLoading: (isLoading: boolean) => void;
  refreshUserInfo: (currentUser: User) => Promise<void>;

  loginWithEmail: (
    credentials: LoginCredentials
  ) => Promise<MultiFactorError | FirebaseError | void>;
  loginWithGoogle: () => Promise<MultiFactorError | FirebaseError | void>;
  logout: () => Promise<void>;

  handleResendVerificationEmail: (email: string) => Promise<boolean>;
  handleVerifyActionCode: (code: string) => Promise<boolean>;
  handleSignInWithEmailLink: (code: string) => Promise<boolean>;

  handleSendPasswordResetEmail: (email: string) => Promise<void>;
  handleVerifyPasswordResetCode: (code: string) => Promise<string>;
  handleConfirmPasswordReset: (
    code: string,
    newPassword: string
  ) => Promise<boolean>;
  remainingSessionTime: number | null;
};

type IAuthProviderProps = {
  children: ReactNode;
};

const AuthContext = createContext<IAuthContext | null>(null);

const AuthProvider: React.FC<IAuthProviderProps> = ({ children }) => {
  const [userInfo, setUserInfo] = useState<any>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [remainingSessionTime, setRemainingSessionTime] = useState<
    number | null
  >(null);
  const { logAction, logError } = useHyperDxAction();
  const [role, setRole] = useState<UserRole | undefined>(undefined);

  const loginWithEmail = async (credentials: LoginCredentials) => {
    await handleLoginWithEmail(credentials, setUserInfo);
    logAction("loginWithEmail", {
      email: credentials.email,
    });
  };

  const loginWithGoogle = async () => {
    await handleLoginWithGoogle();
    logAction("loginWithGoogle", {
      email: auth.currentUser?.email || userInfo?.email || "Unknown",
    });
  };

  const logout = async () => {
    logAction("logout", {
      email: auth.currentUser?.email || userInfo?.email || "Unknown",
    });
    setUserInfo(null);
    setRole(undefined);
    await signOut(auth);
  };

  const handleResendVerificationEmail = async (email: string) => {
    try {
      await apiClient.public.resendVerificationEmail(email);
      logAction("resendVerificationEmail", {
        email: email,
      });
      return true;
    } catch (error) {
      logError(error, {
        action: "resendVerificationEmail",
      });
      console.error("Error resending verification email:", error);
      return false;
    }
  };

  const handleVerifyActionCode = async (code: string) => {
    try {
      await applyActionCode(auth, code);
      logAction("verifyActionCode", {
        code: code,
      });
      return true;
    } catch (error) {
      logError(error, {
        action: "verifyActionCode",
      });
      console.error("[AuthContext] Error verifying action code:", error);
      return false;
    }
  };

  const handleVerifyPasswordResetCode = async (code: string) => {
    try {
      const userEmail = await verifyPasswordResetCode(auth, code);
      logAction("verifyPasswordResetCode", {});
      return userEmail;
    } catch (error) {
      logError(error, {
        action: "verifyPasswordResetCode",
      });
      console.error(
        "[AuthContext] Error verifying password reset code:",
        error
      );
      throw error;
    }
  };

  const handleSendPasswordResetEmail = async (email: string): Promise<void> => {
    const actionCodeSettings = {
      url: "http://localhost:3000",
      handleCodeInApp: false,
    };

    try {
      await sendPasswordResetEmail(auth, email, actionCodeSettings);
      logAction("sendPasswordResetEmail", {
        email: email,
      });
    } catch (error) {
      console.error("Error sending password reset email:", error);
    }
  };

  const handleConfirmPasswordReset = async (
    code: string,
    newPassword: string
  ) => {
    try {
      await confirmPasswordReset(auth, code, newPassword);
      logAction("confirmPasswordReset", {});
      return true;
    } catch (error) {
      logError(error, {
        action: "confirmPasswordReset",
      });
      console.error("Error confirming password reset:", error);
      return false;
    }
  };

  const handleSignInWithEmailLink = async (code: string) => {
    try {
      const urlParams = new URLSearchParams(window.location.search);

      const email = urlParams.get("email");

      if (!email) {
        return false;
      }

      let signInLink = window.location.href.split("&email")[0];

      signInLink = signInLink.replace(
        "http://localhost:3000",
        "https://stg-portal.freetech.co"
      );

      await signInWithEmailLink(auth, email, signInLink);
      logAction("signInWithEmailLink", {
        email: email,
      });
      return true;
    } catch (error) {
      logError(error, {
        action: "signInWithEmailLink",
      });
      return false;
    }
  };

  const checkExpired = (user: User) => {
    user.getIdTokenResult(true).then((tokenResult) => {
      const issuedAt = tokenResult.claims.auth_time;
      if (!issuedAt) {
        console.error("No issuedAt found");
        return;
      }

      const issuedAtInt = parseInt(issuedAt) * 1000; // Convert seconds to milliseconds
      const twoHoursFromIssuedTime =
        issuedAtInt + EXPIRATION_TIME_MINUTES * 60 * 1000; // Add 2 hours to issued time
      const currentTime = Date.now();

      if (currentTime > twoHoursFromIssuedTime) {
        logout();
        window.location.href = "/session-expired";
      } else {
        setRemainingSessionTime(twoHoursFromIssuedTime - currentTime);
      }
    });
  };

  const refreshUserInfo = async (currentUser: User) => {
    if (!currentUser) return;
    checkExpired(currentUser);

    const role = (await auth.currentUser?.getIdTokenResult(false))?.claims
      .role as UserRole;

    setRole(role);

    if (!role) {
      throw new Error("No role found");
    }

    const docSnapData = await getAuthUserProfile(currentUser, role);

    const verified = currentUser.emailVerified;
    const data = { ...docSnapData, email_verified: verified };

    if (docSnapData) {
      setUserInfo((prev: PrivateUserInfo | Freelancer | Stakeholder) => ({
        ...prev,
        ...data,
      }));
    }
  };

  useEffect(() => {
    setIsLoading(true);

    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      setIsLoading(true);
      try {
        if (user && auth.currentUser) {
          if (user?.email) {
            HyperDX.setGlobalAttributes({
              userId: user.uid,
              userEmail: user.email,
            });
          }

          await refreshUserInfo(auth.currentUser);
        } else {
          setUserInfo(null);
          setRole(undefined);
        }
      } catch (error) {
        console.error("Auth state change error:", error);

        if (!auth.currentUser) {
          setUserInfo(null);
          setRole(undefined);
        }
      } finally {
        setIsLoading(false);
      }
    });

    return () => {
      unsubscribe();
    };
  }, []);

  return (
    <AuthContext.Provider
      value={{
        currentUser: auth.currentUser,
        userInfo,
        role,
        refreshUserInfo,
        isLoading,
        setIsLoading,
        loginWithEmail,
        loginWithGoogle,
        logout,
        handleResendVerificationEmail,
        handleSendPasswordResetEmail,
        handleVerifyPasswordResetCode,
        handleConfirmPasswordReset,
        handleVerifyActionCode,
        handleSignInWithEmailLink,
        remainingSessionTime,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export { AuthProvider, AuthContext };
